import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { PanelMenu } from "./PanelMenu";
import { AccountMenu } from "./AccountMenu";
import { PhotoViewer } from "./PhotoViewer";
import {
  store, SERVICE_KINDS, prettyKind, normalizeKind, fmtNum, fmtDate, vehicleSubtitle,
  economyUnit, pctLabel, vehicleShortName, todayLocal, serviceStatus,
  type Vehicle, type Fillup, type Service, type GarageSignals, type FactTone,
} from "./store";
import { syncReminders } from "./reminders";
import { narrateGarage, askGarage, openWebLogin, BrainError, type BrainErrorCode } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { buildExport } from "./export";
import { BackupMenu } from "./BackupMenu";
// "the gauge, honestly read" — the restructured home. See gauge.tsx for the a3 asks R1-R6
// and, above all, THE FENCE: elapsed facts only, never "due"/"overdue".
import { OdoHero, SinceLastService, EconomyLine, VehiclePill, currentOdometer } from "./gauge";
import Chat from "./chat/Chat";
import {
  Sparkles, Fuel, Wrench, Trash2, X, Lock, Car, WifiOff, RefreshCw, Plus, Gauge,
  MessageCircleQuestion, TrendingUp, TrendingDown, Coins, CalendarClock, Route, Check, Copy,
} from "lucide-react";

const LIST_CAP = 50000;

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2 #62, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-garage://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // oriora-garage://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented) {
    return <AiConsent brand="Oriora Garage"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted) return <BootSplash />;
  if (!authed) return <Login />;
  return <Home email={email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function BootSplash() {
  return (
    <div style={{ height: "100dvh", display: "grid", placeItems: "center", background: "var(--bg)" }}>
      <div style={{ display: "grid", placeItems: "center", gap: "0.9rem" }}>
        <div className="empty-art" style={{ width: 64, height: 64, borderRadius: 18 }}><Car size={30} /></div>
        <div className="skeleton-line" style={{ width: 120, height: 10 }} />
      </div>
    </div>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card fade-up empty-glow" style={{ width: "100%", maxWidth: 380, padding: "2.25rem 2rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
        <div className="empty-art" style={{ marginBottom: "1rem" }}><Car size={34} /></div>
        <div className="eyebrow" style={{ justifyContent: "center" }}>Oriora Garage</div>
        <h1 className="serif" style={{ fontSize: "1.55rem", margin: "0.5rem 0 0.5rem", letterSpacing: "-0.02em" }}>Your garage, in numbers</h1>
        <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6, marginBottom: "1.5rem" }}>
          Log fuel and service for every vehicle. It's all stored on this device unless you back it up
          — Oriora narrates the numbers when you ask, through your own account.
        </p>
        <button className="btn btn-primary btn-pill" style={{ width: "100%", minHeight: 48 }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const p = platform();
              if (p === "ios") await signInIos("oriora-garage");
              else if (p === "macos") await signInMac("oriora-garage");
              else if (p === "web") window.location.href = "/login?next=/oriora-garage/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
        <p className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "1rem", lineHeight: 1.5 }}>
          Opens Oriora to sign in, then returns here.
        </p>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [fillups, setFillups] = useState<Fillup[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [editVehicle, setEditVehicle] = useState<Vehicle | null>(null);
  const [showFillup, setShowFillup] = useState(false);
  const [editFillup, setEditFillup] = useState<Fillup | null>(null);
  const [showService, setShowService] = useState(false);
  const [editService, setEditService] = useState<Service | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ kind: "fillup" | "service" | "vehicle"; id: string; label: string } | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Chat panel (ADDITIVE — see mount below). onChanged reloads whatever the chat may have
  // touched (a new vehicle, or a fill-up/service on the active one) so no existing screen goes stale.
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const narrateRef = useRef<HTMLDivElement>(null);

  const active = useMemo(() => vehicles.find((v) => v.id === activeId) ?? null, [vehicles, activeId]);

  const loadVehicles = useCallback(async () => {
    const vs = await store.listVehicles();
    setVehicles(vs);
    setLoaded(true);
    setActiveId((cur) => (cur && vs.some((v) => v.id === cur) ? cur : vs[0]?.id ?? null));
  }, []);
  useEffect(() => { void loadVehicles(); }, [loadVehicles]);

  const loadLogs = useCallback(async (vehicleId: string) => {
    const [fs, ss] = await Promise.all([store.listFillups(vehicleId, LIST_CAP), store.listServices(vehicleId, LIST_CAP)]);
    setFillups(fs);
    setServices(ss);
  }, []);
  useEffect(() => {
    if (activeId) void loadLogs(activeId);
    else { setFillups([]); setServices([]); }
  }, [activeId, loadLogs]);

  // Chat-driven mutation may add a vehicle OR log against the active one — refresh both.
  const reloadFromChat = useCallback(async () => {
    await loadVehicles();
    if (activeId) await loadLogs(activeId);
  }, [loadVehicles, loadLogs, activeId]);

  // Service reminders (on-device local notifications) — a FACTUAL heads-up when the time-interval
  // the user SET on a vehicle has passed since its last logged service. States the elapsed fact,
  // never "due"/"overdue" or advice (the observational ceiling). Native only; best-effort.
  useEffect(() => {
    if (platform() === "web") return;
    void (async () => {
      const reminders = [];
      for (const v of vehicles) {
        if (v.service_interval_days <= 0) continue;
        const ss = await store.listServices(v.id, LIST_CAP);
        const last = ss.reduce<Service | null>((m, s) => (m === null || s.ts > m.ts ? s : m), null);
        if (!last) continue;
        const at = new Date(last.ts);
        at.setDate(at.getDate() + v.service_interval_days);
        at.setHours(9, 0, 0, 0);
        reminders.push({
          key: `svc-${v.id}`,
          title: `${v.name} — service reminder`,
          body: `It's been ${v.service_interval_days} days since your last logged service on ${v.name} (the interval you set).`,
          at,
        });
      }
      void syncReminders(reminders);
    })();
  }, [vehicles, services]);

  const doExport = useCallback(() => {
    if (!active) return;
    const { csv, filename } = buildExport(active, fillups, services);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [active, fillups, services]);

  // Native-menu actions (Actions menu in the macOS menu bar): Fill-up / Service / Narrate.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "fillup": if (activeId) { setEditFillup(null); setShowFillup(true); } break;
        case "service": if (activeId) { setEditService(null); setShowService(true); } break;
        case "narrate":
          narrateRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          narrateRef.current?.querySelector<HTMLButtonElement>("button")?.click();
          break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, [activeId]);

  async function doDelete() {
    if (!confirmDelete) return;
    const { kind, id } = confirmDelete;
    setConfirmDelete(null);
    if (kind === "fillup") { await store.deleteFillup(id); if (activeId) await loadLogs(activeId); }
    else if (kind === "service") { await store.deleteService(id); if (activeId) await loadLogs(activeId); }
    else if (kind === "vehicle") { await store.deleteVehicle(id); await loadVehicles(); }
  }

  // 🔴 These useMemo hooks MUST sit ABOVE the `!loaded` early return. Placed below it they
  // are skipped on the first (loading) render and run on the second → React #310
  // "rendered more hooks than during the previous render", which the ErrorBoundary catches
  // as a blank "Something went wrong" screen. tsc cannot see this; only running it can.
  // Figures the odometer readout leads with (R1). Both are plain arithmetic over the user's own
  // records — statements of what HAS been spent, never a projection of what will be.
  const monthSpend = useMemo(() => {
    const since = Date.now() - 30 * 86_400_000;
    const rows = [
      ...fillups.filter(f => new Date(f.ts).getTime() >= since).map(f => f.cost),
      ...services.filter(s => new Date(s.ts).getTime() >= since).map(s => s.cost),
    ].filter(n => n > 0);
    return rows.length ? rows.reduce((a, b) => a + b, 0) : null;
  }, [fillups, services]);

  const costPerDistance = useMemo(() => {
    const withOdo = fillups.filter(f => f.odometer > 0).sort((a, b) => a.odometer - b.odometer);
    if (withOdo.length < 2) return null;
    const dist = withOdo[withOdo.length - 1].odometer - withOdo[0].odometer;
    // Skip the first fill: its fuel powered the distance BEFORE our window started.
    const spend = withOdo.slice(1).reduce((a, f) => a + (f.cost > 0 ? f.cost : 0), 0);
    return dist > 0 && spend > 0 ? spend / dist : null;
  }, [fillups]);

  if (!loaded) return <BootSplash />;

  const hasData = fillups.length + services.length > 0;

  // 🔴 5.1.1(v): ONE definition, rendered by BOTH branches below. Built as a const rather than
  // repeated inline so the empty state can never silently drift out of having an account control
  // again — that regression is what made deletion unreachable on a fresh install.
  const panelControls = (
    <PanelMenu
      backup={
        <BackupMenu
          appName="Garage" fileSlug="garage" appScheme="oriora-garage"
          hasData={hasData} onRestored={reloadFromChat}
        />
      }
      account={
        <AccountMenu email={email} appSlug="oriora-garage" appName="Oriora Garage" onSignedOut={onSignedOut} />
      }
    />
  );

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      // Safe-area aware so content clears the iPhone notch/home-indicator (viewport-fit=cover).
      padding: "max(1.5rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
      {offline && (
        <div role="status" className="notice notice-warn" style={{ marginBottom: "1rem" }}>
          <WifiOff size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span><strong>Offline.</strong> AI narration is paused — your log still saves on this device.</span>
        </div>
      )}

      {/* 🔴 IDENTITY (2026-08-30): there is NO wordmark header row. The odometer readout IS the
          header — an instrument panel opens with the reading, not with app furniture. The two
          account/backup controls still have to be reachable, so they ride INSIDE the instrument
          cluster (OdoHero) rather than in a wordmark bar above it. Removing this row is the
          change; do not reintroduce a wordmark strip here. */}

      {vehicles.length === 0 ? (
        <EmptyGarage onAdd={() => setShowAddVehicle(true)} controls={panelControls}
                     onStarted={loadVehicles} onAsk={() => setChatOpen(true)} />
      ) : (
        <>
          {/* R3 — the switcher is a compact pill now, not a full-width tab row */}
          <VehiclePill vehicles={vehicles} activeId={activeId} onSelect={setActiveId}
                       onAdd={() => setShowAddVehicle(true)}
                       controls={panelControls} />

          {active && (
            <>
              {/* R1 + R2 — the odometer readout leads. The car's state, not app furniture. */}
              <OdoHero vehicle={active} fillups={fillups} services={services}
                       monthCost={monthSpend} costPerDist={costPerDistance}
                       onEdit={() => setEditVehicle(active)}
                       onAsk={() => setChatOpen(true)} />

              {/* R4 — elapsed fact per service kind + the user's own interval as a passive tick.
                  🔴 never "due"/"overdue" — see the fence in gauge.tsx */}
              <SinceLastService vehicle={active} services={services} fillups={fillups} />

              {/* R5 — economy as a line over distance */}
              <EconomyLine vehicle={active} fillups={fillups} />

              {/* The two logging actions, after the state readout the user came to see */}
              <div className="row" style={{ gap: "0.5rem", marginTop: "1rem" }}>
                <button className="fab" style={{ flex: 1, justifyContent: "center" }}
                  onClick={() => { setEditFillup(null); setShowFillup(true); }}>
                  <Fuel size={17} /> Log fill-up
                </button>
                <button className="btn fab-secondary" style={{ flex: 1, justifyContent: "center" }}
                  onClick={() => { setEditService(null); setShowService(true); }}>
                  <Wrench size={16} /> Log service
                </button>
              </div>
            </>
          )}

          {active && (
            <>

              {hasData && <InsightsPanel vehicle={active} fillups={fillups} services={services} />}

              <div ref={narrateRef}>
                <GarageNarrationCard vehicle={active} fillups={fillups} services={services} />
              </div>

              <AskGarageCard vehicle={active} fillups={fillups} services={services} />

              {/* Fill-ups */}
              <SectionHeader
                icon={<Fuel size={17} style={{ color: "var(--accent)" }} />}
                title="Fill-ups"
                onExport={hasData ? doExport : undefined}
              />
              {fillups.length === 0 ? (
                <EmptyTile icon={<Fuel size={26} />} title="No fill-ups yet" sub="Tap “Log fill-up” after your next stop at the pump." />
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
                  {fillups.slice(0, 25).map((f) => (
                    <FillupTile key={f.id} f={f} vehicle={active} onEdit={() => { setEditFillup(f); setShowFillup(true); }}
                      onDelete={() => setConfirmDelete({ kind: "fillup", id: f.id, label: `the ${fmtDate(f.ts)} fill-up` })} />
                  ))}
                  {fillups.length > 25 && <div className="muted" style={{ fontSize: "var(--fs-xs)", textAlign: "center", padding: "0.5rem" }}>Showing the latest 25 of {fillups.length} · export the full log for the rest</div>}
                </div>
              )}

              {/* Services */}
              <SectionHeader icon={<Wrench size={17} style={{ color: "var(--accent)" }} />} title="Service & repairs"
                action={<button className="btn btn-sm btn-pill" onClick={() => { setEditService(null); setShowService(true); }}><Wrench size={13} /> Log service</button>} />
              {services.length === 0 ? (
                <EmptyTile icon={<Wrench size={26} />} title="No service events yet" sub="Log an oil change, tires, or any repair to track your own intervals." />
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
                  {services.slice(0, 25).map((s) => (
                    <ServiceTile key={s.id} s={s} vehicle={active} onEdit={() => { setEditService(s); setShowService(true); }}
                      onDelete={() => setConfirmDelete({ kind: "service", id: s.id, label: `the ${prettyKind(s.kind)} on ${fmtDate(s.ts)}` })} />
                  ))}
                  {services.length > 25 && <div className="muted" style={{ fontSize: "var(--fs-xs)", textAlign: "center", padding: "0.5rem" }}>Showing the latest 25 of {services.length}</div>}
                </div>
              )}

              <div className="row" style={{ marginTop: "1.5rem", justifyContent: "center" }}>
                <button className="btn-ghost btn-sm" style={{ color: "var(--rose)" }}
                  onClick={() => setConfirmDelete({ kind: "vehicle", id: active.id, label: `${active.name} and all its fill-ups + service history` })}>
                  Remove this vehicle
                </button>
              </div>
            </>
          )}
        </>
      )}

      {showAddVehicle && (
        <VehicleForm editing={null} onClose={() => setShowAddVehicle(false)}
          onSaved={() => { setShowAddVehicle(false); void loadVehicles(); }} />
      )}
      {editVehicle && (
        <VehicleForm editing={editVehicle} onClose={() => setEditVehicle(null)}
          onSaved={() => { setEditVehicle(null); void loadVehicles(); }} />
      )}
      {showFillup && active && (
        <FillupForm vehicle={active} editing={editFillup} lastOdometer={fillups[0]?.odometer ?? null}
          onClose={() => { setShowFillup(false); setEditFillup(null); }}
          onSaved={() => { setShowFillup(false); setEditFillup(null); if (activeId) void loadLogs(activeId); }} />
      )}
      {showService && active && (
        <ServiceForm vehicle={active} editing={editService} lastOdometer={fillups[0]?.odometer ?? services[0]?.odometer ?? null}
          onClose={() => { setShowService(false); setEditService(null); }}
          onSaved={() => { setShowService(false); setEditService(null); if (activeId) void loadLogs(activeId); }} />
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the
          launcher + slide-over live on top; no existing screen is altered. A chat-driven change
          reloads the vehicle list + the active vehicle's log. */}
      {/* 🔴 IDENTITY (2026-08-30): the floating "✨ Chat" pill is GONE — it was the single most
          cloned element in the fleet (same pill, same bottom-right corner, same word, ~15 apps).
          The assistant is now an instrument READING inside the gauge cluster (see OdoHero's
          "ASK" dial). Do not reintroduce a floating launcher. */}
      <aside className={`grc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="grc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to log, find, or narrate</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat vehicleId={activeId} vehicleName={active?.name ?? null} onChanged={reloadFromChat} />
      </aside>

      {confirmDelete && (
        <CenterModal title="Delete?" onClose={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.5 }}>
            Delete {confirmDelete.label}? This can’t be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-primary btn-pill" style={{ background: "var(--rose)", borderColor: "var(--rose)" }} onClick={doDelete}>Delete</button>
          </div>
        </CenterModal>
      )}
    </main>
  );
}

function EmptyGarage({ onAdd, controls, onStarted, onAsk }: { onAdd: () => void;
  /** Backup + account — the SAME PanelMenu the vehicle row carries once a vehicle exists.
   *  🔴 5.1.1(v): these used to live only in the non-empty branch, so a fresh install (what an
   *  Apple reviewer sees) had no avatar, no sign-out and NO WAY TO DELETE THE ACCOUNT.
   *
   *  It rides on its own row ABOVE the card, not inside it, for two reasons:
   *   • `.empty-glow` is `overflow: hidden` (it clips the radial glow to the card's corners), so a
   *     control mounted inside the card would have its popover CLIPPED — the exact trap styles.css
   *     already warns about at `.odo-menu-row > div`.
   *   • It mirrors VehiclePill's own row geometry (controls right, same 0.85rem rhythm). In the
   *     empty state that row simply has no vehicle pill on the left yet.
   *  This is NOT a wordmark strip — no app name, no furniture, just the one instrument control. */
  controls?: ReactNode;
  /** Reload the garage once the first vehicle + reading exist. */
  onStarted: () => Promise<void> | void;
  /** The assistant entry, which the empty state previously did not carry at all. */
  onAsk: () => void }) {
  return (
    <>
      {controls && (
        <div className="row" style={{ justifyContent: "flex-end", marginBottom: "0.85rem" }}>{controls}</div>
      )}
      {/* READY-STATE 2026-09-19 — a fresh install used to be the fleet's shared empty card (car
          glyph → serif line → two grey lines → "Add vehicle"), which opened a blank vehicle form
          and STILL left the gauge dark: the odometer only exists once a reading is logged, so the
          old door took two forms to light anything. The ledger says the odometer IS the header, so
          the first screen is now the cluster itself, waiting for its reading, with the reading as
          the door — and with the two BETTER rows aimed at the leaders' counted data-loss and
          paid-export complaints (PARITY-MATRIX rows 17 + 18) stated on it. */}
      <FirstReading onStarted={onStarted} onAddByForm={onAdd} onAsk={onAsk} />
    </>
  );
}

// The first screen's instrument cluster + the one-line door. Uses the app's own .odo-* vocabulary
// (gauge.tsx owns the populated hero and is NOT touched). No AI call: row 16 narration is billed
// and stays behind the assistant, exactly as it is once a vehicle exists.
function FirstReading({ onStarted, onAddByForm, onAsk }: {
  onStarted: () => Promise<void> | void; onAddByForm: () => void; onAsk: () => void;
}) {
  const [name, setName] = useState("");
  const [plate, setPlate] = useState("");
  const [odo, setOdo] = useState("");
  const [unit, setUnit] = useState<"mi" | "km">("mi");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const odoNum = Number(odo.replace(/[^0-9.]/g, "")) || 0;

  async function start() {
    const n = name.trim();
    if (!n) { setErr("Give the vehicle a name — everything else can change later."); return; }
    setBusy(true); setErr(null);
    try {
      const v = await store.createVehicle({
        name: n, plate: plate.trim(), distance_unit: unit,
      });
      // The odometer lives on the LOG, not on the vehicle, so a reading has to be an entry. This
      // one carries no fuel, so it is recorded as `is_full_tank: false` — that is the flag both
      // signals paths key on (signals.rs / signals-web.ts), which keeps a fuel-less reading out of
      // the economy maths instead of poisoning it with a 0-litre tank.
      if (odoNum > 0) {
        await store.createFillup({
          vehicle_id: v.id, odometer: odoNum, volume: 0, cost: 0,
          is_full_tank: false, note: "Starting odometer reading",
        });
      }
      await onStarted();
    } catch (e) {
      setErr(`Couldn't start the log — ${e instanceof Error ? e.message : String(e)}`);
      setBusy(false);
    }
  }

  return (
    <>
      <div className="card fade-up empty-glow gar-first" style={{ borderRadius: "var(--radius-lg)" }}>
        <div className="gar-first__dial" aria-hidden>
          <Gauge size={20} />
        </div>
        <div className="gar-first__odo tnum">
          {odoNum > 0 ? fmtNum(odoNum) : "000000"}<span className="gar-first__unit"> {unit} on the clock</span>
        </div>
        <div className="gar-first__lede">
          The odometer is the header here. Give it the reading on your dash right now and this
          becomes your car's own record — every fill-up and every service, in your numbers.
        </div>

        <div className="gar-first__rule"><span>First reading</span></div>

        <div className="gar-first__row">
          <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Vehicle name"
            placeholder="Vehicle — e.g. the Golf" className="gar-first__name"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void start(); } }} />
          <input value={plate} onChange={(e) => setPlate(e.target.value)} aria-label="Number plate (optional)"
            placeholder="plate" className="gar-first__plate" />
        </div>

        <div className="odo-field gar-first__field">
          <Gauge size={18} className="odo-needle" style={{ color: "var(--accent)", flexShrink: 0 }} />
          <input value={odo} onChange={(e) => setOdo(e.target.value)} inputMode="numeric"
            aria-label={`Odometer now (${unit})`} placeholder="Odometer now"
            className="odo-value gar-first__odoin"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void start(); } }} />
          <div className="gar-first__units" role="group" aria-label="Distance unit">
            {(["mi", "km"] as const).map((u) => (
              <button key={u} type="button" className={`gar-first__unitbtn${unit === u ? " on" : ""}`}
                onClick={() => setUnit(u)}>{u}</button>
            ))}
          </div>
        </div>

        {err && <div className="gar-first__err">{err}</div>}

        <button className="btn btn-primary btn-pill gar-first__go" disabled={busy} onClick={() => void start()}>
          <Plus size={17} /> {busy ? "Starting…" : "Start the log"}
        </button>

        <div className="gar-first__foot">
          Your numbers stay on this device. <strong>CSV export is free</strong>, and the backup goes
          to <strong>your own</strong> Drive or GitHub — there is no account holding your history
          hostage, and nothing to lose if we vanish.
          {" "}
          <button className="btn-ghost gar-first__alt" onClick={onAddByForm}>Fill in the full details instead</button>
        </div>
      </div>

      {/* The assistant entry — the empty state carried NO way to reach it, so on a fresh install
          the chat existed with no door. Same .odo-ask instrument the populated cluster uses. */}
      <button type="button" className="odo-ask" onClick={onAsk} aria-label="Ask your garage">
        <span className="odo-ask-dial" aria-hidden><MessageCircleQuestion size={16} /></span>
        <span className="odo-ask-txt">
          <span className="odo-ask-lbl">Ask</span>
          <span className="odo-ask-sub">log a fill-up · find a service · read my numbers back</span>
        </span>
      </button>
    </>
  );
}

function VehicleSwitcher({ vehicles, activeId, onSelect, onAdd }: { vehicles: Vehicle[]; activeId: string | null; onSelect: (id: string) => void; onAdd: () => void }) {
  return (
    <div style={{ marginBottom: "0.9rem" }}>
      <div className="seg" role="tablist" aria-label="Vehicles">
        {vehicles.map((v) => (
          <button key={v.id} role="tab" aria-selected={v.id === activeId} className={`seg-item${v.id === activeId ? " selected" : ""}`} onClick={() => onSelect(v.id)}>{vehicleShortName(v)}</button>
        ))}
        <button className="seg-item seg-add" onClick={onAdd} aria-label="Add vehicle"><Plus size={15} /></button>
      </div>
    </div>
  );
}

function VehicleHero({ vehicle, fillups, services, onEdit }: { vehicle: Vehicle; fillups: Fillup[]; services: Service[]; onEdit: () => void }) {
  const subtitle = vehicleSubtitle(vehicle);
  const latestOdo = useMemo(() => fillups.reduce<number | null>((m, f) => (m === null ? f.odometer : Math.max(m, f.odometer)), null), [fillups]);
  const lastService = useMemo(() => services.reduce<Service | null>((m, s) => (m === null || s.ts > m.ts ? s : m), null), [services]);
  const svc = serviceStatus(vehicle, lastService, latestOdo);
  const svcNote = svc && (svc.interval_km_reached || svc.interval_days_reached)
    ? [svc.interval_km_reached && svc.km_since != null ? `${fmtNum(svc.km_since)} ${vehicle.distance_unit} since last service` : null,
       svc.interval_days_reached && svc.days_since != null ? `${svc.days_since} days since last service` : null].filter(Boolean).join(" · ")
    : null;
  return (
    <div className="hero fade-up" style={{ marginBottom: "1rem" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", position: "relative", zIndex: 1 }}>
        <div style={{ minWidth: 0 }}>
          <div className="serif" style={{ fontSize: "1.7rem", fontWeight: 600, lineHeight: 1.1, letterSpacing: "-0.02em" }}>{vehicle.name}</div>
          {subtitle && <div style={{ fontSize: "var(--fs-sm)", opacity: 0.8, marginTop: 3 }}>{subtitle}{vehicle.plate ? ` · ${vehicle.plate}` : ""}</div>}
        </div>
        <button className="btn btn-on-hero btn-sm btn-pill" onClick={onEdit}>Edit</button>
      </div>
      <div className="row" style={{ gap: "1.4rem", marginTop: "1rem", fontSize: "var(--fs-sm)", position: "relative", zIndex: 1 }}>
        <span className="row tnum" style={{ gap: "0.4rem" }}><Gauge size={16} /> {latestOdo !== null ? `${fmtNum(latestOdo)} ${vehicle.distance_unit}` : "no odometer yet"}</span>
        <span className="row tnum" style={{ gap: "0.4rem" }}><Fuel size={16} /> {fillups.length}</span>
        <span className="row tnum" style={{ gap: "0.4rem" }}><Wrench size={16} /> {services.length}</span>
      </div>
      {svcNote && (
        <div className="row" style={{ gap: "0.4rem", marginTop: "0.7rem", fontSize: "var(--fs-xs)", background: "rgba(255,255,255,0.14)", padding: "0.35rem 0.6rem", borderRadius: 10, position: "relative", zIndex: 1 }}>
          <Wrench size={13} /> {svcNote} — the reminder interval you set on this vehicle.
        </div>
      )}
    </div>
  );
}

// ── Insights panel: the computed-figures dashboard (deterministic, no AI call) ──
function InsightsPanel({ vehicle, fillups, services }: { vehicle: Vehicle; fillups: Fillup[]; services: Service[] }) {
  const [signals, setSignals] = useState<GarageSignals | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    store.computeSignals(vehicle.id, todayLocal())
      .then((s) => { if (alive) { setSignals(s); setLoading(false); } })
      .catch(() => { if (alive) { setSignals(null); setLoading(false); } });
    return () => { alive = false; };
  }, [vehicle.id, fillups.length, services.length]);

  if (loading) {
    return (
      <div className="card" style={{ padding: "1rem", marginBottom: "1rem" }}>
        <div className="stat-grid">
          {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton-block" style={{ height: 78 }} />)}
        </div>
      </div>
    );
  }
  if (!signals) return null;

  const eco = signals.economy;
  const cpd = signals.cost_per_distance;
  const sp = signals.spend;
  const proj = signals.projection;
  const ecoUnit = economyUnit(vehicle);

  return (
    <div className="fade-up" style={{ marginBottom: "1.1rem" }}>
      <div className="stat-grid" style={{ marginBottom: "0.7rem" }}>
        <StatCard
          label={<><Gauge size={13} /> Fuel economy</>}
          value={eco.recent_avg !== null ? eco.recent_avg.toFixed(1) : "—"}
          unit={eco.recent_avg !== null ? ecoUnit : undefined}
          trend={eco.direction && eco.delta !== null && eco.direction !== "flat"
            ? { dir: eco.direction === "improving" ? "up" : "down", good: eco.direction === "improving", label: `${eco.delta > 0 ? "+" : ""}${eco.delta.toFixed(1)}` }
            : null}
          sub={eco.recent_avg === null ? "Need 2 full-tank fills" : (eco.lifetime_avg !== null ? `lifetime ${eco.lifetime_avg.toFixed(1)}` : undefined)}
        />
        <StatCard
          label={<><Coins size={13} /> Cost / {vehicle.distance_unit}</>}
          value={cpd.recent !== null ? cpd.recent.toFixed(2) : "—"}
          trend={cpd.direction && cpd.direction !== "flat" && cpd.pct_change !== null
            ? { dir: cpd.direction, good: cpd.direction === "down", label: pctLabel(cpd.pct_change) ?? "" }
            : null}
          sub={cpd.recent === null ? "Add fuel costs to track" : "fuel only"}
        />
        <StatCard
          label={<><CalendarClock size={13} /> Spent / month</>}
          value={sp.avg_per_month !== null ? fmtNum(sp.avg_per_month) : "—"}
          trend={sp.direction && sp.direction !== "flat" && sp.total_pct_change_12mo !== null
            ? { dir: sp.direction, good: sp.direction === "down", label: pctLabel(sp.total_pct_change_12mo) ?? "" }
            : null}
          sub={sp.total_lifetime > 0 ? `${fmtNum(sp.total_lifetime)} lifetime` : "fuel + service"}
        />
        <StatCard
          label={<><Route size={13} /> Pace</>}
          value={proj ? fmtNum(proj.avg_distance_per_month) : "—"}
          unit={proj ? `${vehicle.distance_unit}/mo` : undefined}
          sub={proj ? `~${fmtNum(proj.projected_odometer_in_12mo)} in 12mo` : "More history needed"}
        />
      </div>

      {eco.points.length >= 2 && <EconomySparkline points={eco.points} unit={ecoUnit} best={eco.best} />}

      {signals.spend_by_category.length > 0 && <SpendBreakdown signals={signals} />}
    </div>
  );
}

function StatCard({ label, value, unit, trend, sub }: {
  label: React.ReactNode; value: string; unit?: string;
  trend?: { dir: "up" | "down"; good: boolean; label: string } | null; sub?: string;
}) {
  return (
    <div className="stat">
      <div className="stat-lbl">{label}</div>
      <div className="stat-val tnum">{value}{unit && <span className="unit">{unit}</span>}</div>
      {trend && trend.label ? (
        <div className="stat-sub tnum" style={{ color: trend.good ? "var(--good)" : "var(--bad)" }}>
          {trend.dir === "up" ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {trend.label}
        </div>
      ) : sub ? (
        <div className="stat-sub tnum">{sub}</div>
      ) : null}
    </div>
  );
}

function EconomySparkline({ points, unit, best }: { points: { date: string; economy: number }[]; unit: string; best: number | null }) {
  const recent = points.slice(-12);
  const max = Math.max(...recent.map((p) => p.economy), 1);
  return (
    <div className="card" style={{ padding: "0.9rem 1rem", marginBottom: "0.7rem" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.7rem" }}>
        <div className="stat-lbl" style={{ margin: 0 }}>Economy per fill</div>
        <div className="muted tnum" style={{ fontSize: "var(--fs-xs)" }}>{unit}</div>
      </div>
      <div className="spark-bars" role="img" aria-label={`Fuel economy across the last ${recent.length} fill-ups`}>
        {recent.map((p, i) => {
          const isPeak = best !== null && Math.abs(p.economy - best) < 0.05;
          return (
            <div key={i} className="spark-col" title={`${fmtDate(p.date)} · ${p.economy.toFixed(1)} ${unit}`}>
              <div className={`spark-bar${isPeak ? " peak" : ""}`} style={{ height: `${Math.max(6, (p.economy / max) * 100)}%` }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SpendBreakdown({ signals }: { signals: GarageSignals }) {
  const cats = signals.spend_by_category.filter((c) => c.recent > 0).slice(0, 5);
  if (cats.length === 0) return null;
  const max = Math.max(...cats.map((c) => c.recent), 1);
  return (
    <div className="card" style={{ padding: "0.9rem 1rem" }}>
      <div className="stat-lbl" style={{ marginBottom: "0.6rem" }}>Service spend · last 12 months</div>
      {cats.map((c) => (
        <div key={c.kind} className="spend-row">
          <span className="spend-name">{prettyKind(c.kind)}</span>
          <div className="spend-track"><div className="spend-fill" style={{ width: `${Math.max(6, (c.recent / max) * 100)}%` }} /></div>
          <span className="spend-val tnum">{fmtNum(c.recent)}</span>
        </div>
      ))}
    </div>
  );
}

// ── The billed AI narration: describes the on-device-computed figures (no advice) ──
type NarrateErr = { code: BrainErrorCode | "empty"; message: string };

function GarageNarrationCard({ vehicle, fillups, services }: { vehicle: Vehicle; fillups: Fillup[]; services: Service[] }) {
  const [text, setText] = useState<string | null>(null);
  const [signals, setSignals] = useState<GarageSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(false);
  const [err, setErr] = useState<NarrateErr | null>(null);

  // Reset when the vehicle changes — the narration is per-vehicle.
  useEffect(() => { setText(null); setSignals(null); setErr(null); setFresh(false); }, [vehicle.id]);

  const hasData = fillups.length + services.length > 0;

  async function run() {
    setBusy(true); setErr(null); setText(null); setSignals(null); setFresh(false);
    try {
      // Compute the signals locally first (deterministic, on-device), then send ONLY those.
      const sig = await store.computeSignals(vehicle.id, todayLocal());
      const r = await narrateGarage(sig);
      const narration = r.narration.trim();
      if (!narration) {
        setErr({ code: "empty", message: "Not enough logged yet to narrate — add a couple more fill-ups." });
      } else {
        setText(narration);
        setSignals(sig);
        setFresh(true);
        setTimeout(() => setFresh(false), 700);
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Narration failed — please try again." });
    } finally { setBusy(false); }
  }

  if (!hasData) return null;
  return (
    <div className={`recap${fresh ? " fresh" : ""}`} style={{ padding: "1.1rem 1.25rem", marginBottom: "1rem" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
        <div className="eyebrow" style={{ flexWrap: "wrap" }}>
          <Sparkles size={14} style={{ flexShrink: 0 }} /> Garage recap
        </div>
        <button className="btn btn-primary btn-sm btn-pill" disabled={busy} onClick={run} style={{ flexShrink: 0 }}>
          {busy ? "Reading…" : text ? (<><RefreshCw size={12} /> Refresh</>) : "Narrate"}
        </button>
      </div>

      {!busy && !text && !err && (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8, lineHeight: 1.55 }}>
          A plain-words read of your fuel economy, cost-per-{vehicle.distance_unit}, and service intervals — facts only, no advice.
        </div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.85rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} className="pulse-soft" /> Reading your numbers…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "94%" }} />
          <div className="skeleton-line" style={{ width: "68%" }} />
          {/* Reserve the facts column too, so the result lands without a layout shift. */}
          <div style={{ marginTop: 6, paddingTop: 8, borderTop: "1px solid var(--border-subtle)", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
            <div className="skeleton-line" style={{ width: "82%", height: 9 }} />
            <div className="skeleton-line" style={{ width: "74%", height: 9 }} />
          </div>
        </div>
      )}

      {err && !busy && <BrainErr err={err} onRetry={run} />}

      {text && !busy && (
        <div className="fade-up" style={{ marginTop: "0.7rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
          {signals && <FactList signals={signals} />}
          <div className="row" style={{ marginTop: 12, justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>Describes your own logged figures — not mechanical, maintenance, or financial advice.</span>
            <CopyButton text={copyableNarration(text, signals)} />
          </div>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
        </div>
      )}
    </div>
  );
}

// "Ask your garage" — repeat billable Q&A over the same on-device signal slice.
function AskGarageCard({ vehicle, fillups, services }: { vehicle: Vehicle; fillups: Fillup[]; services: Service[] }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<NarrateErr | null>(null);

  useEffect(() => { setAnswer(null); setErr(null); setQuestion(""); }, [vehicle.id]);

  const hasData = fillups.length + services.length > 0;
  const suggestions = [
    "How much has this car cost me this year?",
    "When did I last replace the tires?",
    "What's my fuel economy lately?",
  ];

  async function ask(q: string) {
    const text = q.trim();
    if (!text) return;
    setBusy(true); setErr(null); setAnswer(null);
    try {
      const sig = await store.computeSignals(vehicle.id, todayLocal());
      const r = await askGarage(sig, text);
      setAnswer(r.answer || "I couldn't find that in your logged data.");
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "That failed — please try again." });
    } finally { setBusy(false); }
  }

  if (!hasData) return null;
  return (
    <div className="card" style={{ padding: "1.1rem 1.25rem", marginBottom: "1.25rem" }}>
      <div className="eyebrow" style={{ marginBottom: "0.7rem" }}>
        <MessageCircleQuestion size={15} /> Ask your garage
      </div>
      <form className="row" style={{ gap: "0.5rem", flexWrap: "nowrap" }} onSubmit={(e) => { e.preventDefault(); void ask(question); }}>
        <input placeholder="Ask about your own log…" value={question} onChange={(e) => setQuestion(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
        <button type="submit" className="btn btn-primary btn-pill" style={{ flexShrink: 0, padding: "0 1.1rem" }} disabled={busy || !question.trim()}>{busy ? "…" : "Ask"}</button>
      </form>

      {!answer && !err && !busy && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.7rem" }}>
          {suggestions.map((s) => (
            <button key={s} className="chip chip-suggest" onClick={() => { setQuestion(s); void ask(s); }}>{s}</button>
          ))}
        </div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.8rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "78%" }} />
        </div>
      )}

      {err && !busy && <BrainErr err={err} onRetry={() => void ask(question)} />}

      {answer && !busy && (
        <div className="fade-up" style={{ marginTop: "0.8rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{answer}</p>
          <div className="row" style={{ marginTop: 10, justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>Answers from your logged figures only — not mechanical or financial advice.</span>
            <CopyButton text={answer} />
          </div>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
        </div>
      )}
    </div>
  );
}

// Ghost copy-button for AI results (DESIGN-SYSTEM §7.5 — every Oriora result is copyable).
// Swaps glyph → check + "Copied" for 1.5s; aria-live announces it.
function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* clipboard blocked — silently no-op */ }
  }, [text]);
  return (
    <button className="copy-btn" onClick={copy} aria-label={done ? "Copied" : label} title={done ? "Copied" : label}>
      <span aria-hidden>{done ? <Check size={13} /> : <Copy size={13} />}</span>
      <span className="copy-btn-lbl">{done ? "Copied" : label}</span>
      <span aria-live="polite" className="sr-only">{done ? "Copied to clipboard" : ""}</span>
    </button>
  );
}

// Build the plain-text copy payload: the narration plus its grounding facts (no markup).
function copyableNarration(text: string, signals: GarageSignals | null): string {
  const facts = signals?.headline_facts.slice(0, 5).map((f) => `• ${f.text}`).join("\n") ?? "";
  return facts ? `${text}\n\nThe numbers behind this:\n${facts}` : text;
}

function BrainErr({ err, onRetry }: { err: NarrateErr; onRetry: () => void }) {
  // Insufficient funds — point to the Oriora account on the web, NO amounts shown.
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-warn fade-up" style={{ marginTop: 12 }}>
        <Coins size={16} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Add funds to keep using AI</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then try again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Sparkles size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "unauthenticated") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Lock size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // "refused" = the observational fence declining an advice/due/diagnosis/price ask — an
  // informational line in the fence's voice, never a red error (nothing failed, nothing billed).
  if (err.code === "empty" || err.code === "refused") {
    return <div className="muted fade-up" style={{ fontSize: "var(--fs-sm)", marginTop: 12 }}>{err.message}</div>;
  }
  const retryable = err.code === "unavailable";
  return (
    <div className="notice notice-err fade-up" style={{ marginTop: 12, justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-sm)" }}>{err.message}</span>
      {retryable && (
        <button className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// The receipts: the deterministic facts the narration drew from. Purely observational.
function FactList({ signals }: { signals: GarageSignals }) {
  const facts = signals.headline_facts.slice(0, 5);
  if (facts.length === 0) return null;
  const dotClass = (t: FactTone) => (t === "good" ? "good" : t === "watch" ? "watch" : "");
  return (
    <div style={{ marginTop: "0.85rem", paddingTop: "0.5rem", borderTop: "1px solid var(--border-subtle)" }}>
      <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>The numbers behind this</div>
      {facts.map((f, i) => (
        <div key={i} className="fact">
          <span className={`fact-dot ${dotClass(f.tone)}`} />
          <span className="fact-text">{f.text}</span>
        </div>
      ))}
    </div>
  );
}

// ── List tiles ───────────────────────────────────────────────────────────────
function SectionHeader({ icon, title, onExport, action }: { icon: React.ReactNode; title: string; onExport?: () => void; action?: React.ReactNode }) {
  return (
    <div className="sec-head">
      <h2>{icon}{title}</h2>
      <div className="row" style={{ gap: "0.4rem" }}>
        {action}
        {onExport && <button className="btn btn-sm btn-pill" onClick={onExport}>Export CSV</button>}
        <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.25rem" }}><Lock size={11} /> on device</span>
      </div>
    </div>
  );
}

function EmptyTile({ icon, title, sub }: { icon: React.ReactNode; title: string; sub: string }) {
  return (
    <div className="card" style={{ padding: "1.85rem 1.25rem", textAlign: "center" }}>
      <div className="empty-art" style={{ width: 56, height: 56, borderRadius: 16 }}>{icon}</div>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-md)", margin: "0.7rem 0 0.25rem" }}>{title}</div>
      <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.5, maxWidth: 280, marginInline: "auto" }}>{sub}</div>
    </div>
  );
}

function FillupTile({ f, vehicle, onEdit, onDelete }: { f: Fillup; vehicle: Vehicle; onEdit: () => void; onDelete: () => void }) {
  // A receipt/proof photo attached in chat (on-device only) shows as a thumbnail; tap to zoom.
  const [zoom, setZoom] = useState(false);
  return (
    <>
      <div className="tile" onClick={onEdit} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") onEdit(); }}>
        <div className="tile-icon"><Fuel size={18} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }} className="tnum">{fmtDate(f.ts)} · {fmtNum(f.volume, 1)} {vehicle.volume_unit}</div>
          <div className="muted tnum" style={{ fontSize: "var(--fs-xs)", color: "var(--ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {fmtNum(f.odometer)} {vehicle.distance_unit}{f.cost > 0 ? ` · ${fmtNum(f.cost, 2)}` : ""}{!f.is_full_tank ? " · partial" : ""}{f.note ? ` · ${f.note}` : ""}
          </div>
        </div>
        {f.photo && (
          <img src={f.photo} alt="Fill-up receipt" title="Tap to view"
            onClick={(ev) => { ev.stopPropagation(); setZoom(true); }}
            style={{ width: 40, height: 40, objectFit: "cover", borderRadius: "var(--radius-sm, 8px)", cursor: "zoom-in", flexShrink: 0, marginRight: "0.4rem" }} />
        )}
        <button className="tile-del" title="Delete fill-up" aria-label="Delete fill-up"
          onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
          <Trash2 size={16} />
        </button>
      </div>
      {zoom && f.photo && (
        <PhotoViewer photos={[{ src: f.photo, alt: "Fill-up receipt" }]} onClose={() => setZoom(false)} />
      )}
    </>
  );
}

function ServiceTile({ s, vehicle, onEdit, onDelete }: { s: Service; vehicle: Vehicle; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="tile" onClick={onEdit} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onEdit(); }}>
      <div className="tile-icon"><Wrench size={18} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{prettyKind(s.kind)}</div>
        <div className="muted tnum" style={{ fontSize: "var(--fs-xs)", color: "var(--ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {fmtDate(s.ts)} · {fmtNum(s.odometer)} {vehicle.distance_unit}{s.cost > 0 ? ` · ${fmtNum(s.cost, 2)}` : ""}{s.note ? ` · ${s.note}` : ""}
        </div>
      </div>
      <button className="tile-del" title="Delete service" aria-label="Delete service"
        onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
        <Trash2 size={16} />
      </button>
    </div>
  );
}


// ── Forms ────────────────────────────────────────────────────────────────────
function VehicleForm({ editing, onClose, onSaved }: { editing: Vehicle | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [make, setMake] = useState(editing?.make ?? "");
  const [model, setModel] = useState(editing?.model ?? "");
  const [year, setYear] = useState(editing && editing.year > 0 ? String(editing.year) : "");
  const [plate, setPlate] = useState(editing?.plate ?? "");
  const [distanceUnit, setDistanceUnit] = useState<Vehicle["distance_unit"]>(editing?.distance_unit ?? "mi");
  const [volumeUnit, setVolumeUnit] = useState<Vehicle["volume_unit"]>(editing?.volume_unit ?? "gal");
  const [intervalDist, setIntervalDist] = useState(editing && editing.service_interval_km > 0 ? String(editing.service_interval_km) : "");
  const [intervalMonths, setIntervalMonths] = useState(editing && editing.service_interval_days > 0 ? String(Math.round(editing.service_interval_days / 30)) : "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) { setErr("Give the vehicle a name."); return; }
    setSaving(true); setErr(null);
    const payload = {
      name: name.trim(), make: make.trim(), model: model.trim(),
      year: year ? Math.max(0, parseInt(year, 10) || 0) : 0,
      plate: plate.trim(), distance_unit: distanceUnit, volume_unit: volumeUnit,
      service_interval_km: intervalDist ? Math.max(0, parseInt(intervalDist, 10) || 0) : 0,
      service_interval_days: intervalMonths ? Math.max(0, (parseInt(intervalMonths, 10) || 0) * 30) : 0,
    };
    try {
      if (isEditing) await store.updateVehicle(editing!.id, payload);
      else await store.createVehicle(payload);
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <BottomSheet title={isEditing ? "Edit vehicle" : "Add vehicle"} onClose={onClose}>
      <Field label="Name"><input placeholder="My car" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
      <div className="row" style={{ gap: "0.6rem", alignItems: "stretch", flexWrap: "nowrap" }}>
        <Field label="Make" style={{ flex: 1 }}><input placeholder="Toyota" value={make} onChange={(e) => setMake(e.target.value)} /></Field>
        <Field label="Model" style={{ flex: 1 }}><input placeholder="Corolla" value={model} onChange={(e) => setModel(e.target.value)} /></Field>
      </div>
      <div className="row" style={{ gap: "0.6rem", alignItems: "stretch", flexWrap: "nowrap" }}>
        <Field label="Year" style={{ flex: 1 }}><input inputMode="numeric" placeholder="2018" value={year} onChange={(e) => setYear(e.target.value.replace(/[^0-9]/g, ""))} /></Field>
        <Field label="Plate" style={{ flex: 1 }}><input placeholder="ABC-123" value={plate} onChange={(e) => setPlate(e.target.value)} /></Field>
      </div>
      <div className="row" style={{ gap: "0.6rem", alignItems: "stretch", flexWrap: "nowrap" }}>
        <Field label="Distance" style={{ flex: 1 }}>
          <select value={distanceUnit} onChange={(e) => setDistanceUnit(e.target.value as Vehicle["distance_unit"])}>
            <option value="mi">miles (mi)</option>
            <option value="km">kilometres (km)</option>
          </select>
        </Field>
        <Field label="Volume" style={{ flex: 1 }}>
          <select value={volumeUnit} onChange={(e) => setVolumeUnit(e.target.value as Vehicle["volume_unit"])}>
            <option value="gal">gallons (gal)</option>
            <option value="L">litres (L)</option>
          </select>
        </Field>
      </div>
      <Field label="Service reminder (optional)">
        <div className="row" style={{ gap: "0.6rem", alignItems: "stretch", flexWrap: "nowrap" }}>
          <input inputMode="numeric" placeholder={`every … ${distanceUnit}`} value={intervalDist} onChange={(e) => setIntervalDist(e.target.value.replace(/[^0-9]/g, ""))} />
          <input inputMode="numeric" placeholder="every … months" value={intervalMonths} onChange={(e) => setIntervalMonths(e.target.value.replace(/[^0-9]/g, ""))} />
        </div>
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.35rem" }}>A heads-up when this much distance or time has passed since your last logged service — your own interval.</div>
      </Field>
      {err && <div className="notice notice-err" style={{ margin: "0.5rem 0 0.25rem" }}><span style={{ fontSize: "var(--fs-xs)" }}>{err}</span></div>}
      <FormActions onCancel={onClose} onSave={save} saving={saving} canSave={!!name.trim()} saveLabel={isEditing ? "Update" : "Add vehicle"} />
    </BottomSheet>
  );
}

function FillupForm({ vehicle, editing, lastOdometer, onClose, onSaved }: { vehicle: Vehicle; editing: Fillup | null; lastOdometer: number | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [date, setDate] = useState(editing ? editing.ts.slice(0, 10) : todayLocal());
  const [odometer, setOdometer] = useState(editing ? String(editing.odometer) : "");
  const [volume, setVolume] = useState(editing ? String(editing.volume) : "");
  const [cost, setCost] = useState(editing && editing.cost > 0 ? String(editing.cost) : "");
  const [isFull, setIsFull] = useState(editing ? editing.is_full_tank : true);
  const [note, setNote] = useState(editing?.note ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    const odo = parseFloat(odometer);
    const vol = parseFloat(volume);
    if (Number.isNaN(odo) || odo < 0) { setErr("Enter the odometer reading."); return; }
    if (Number.isNaN(vol) || vol <= 0) { setErr("Enter how much fuel you added."); return; }
    setSaving(true); setErr(null);
    const ts = new Date(date + "T12:00:00").toISOString();
    const payload = { odometer: odo, volume: vol, cost: cost ? Math.max(0, parseFloat(cost) || 0) : 0, is_full_tank: isFull, note: note.trim(), ts };
    try {
      if (isEditing) await store.updateFillup(editing!.id, payload);
      else await store.createFillup({ vehicle_id: vehicle.id, ...payload });
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <BottomSheet title={isEditing ? "Edit fill-up" : "Log fill-up"} onClose={onClose}>
      <div className="row" style={{ gap: "0.6rem", alignItems: "stretch", flexWrap: "nowrap" }}>
        <Field label="Date" style={{ flex: 1 }}><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label={`Odometer (${vehicle.distance_unit})`} style={{ flex: 1 }}>
          <input inputMode="decimal" placeholder={lastOdometer !== null ? `≥ ${fmtNum(lastOdometer)}` : "e.g. 42000"} value={odometer} onChange={(e) => setOdometer(e.target.value.replace(/[^0-9.]/g, ""))} />
        </Field>
      </div>
      <div className="row" style={{ gap: "0.6rem", alignItems: "stretch", flexWrap: "nowrap" }}>
        <Field label={`Volume (${vehicle.volume_unit})`} style={{ flex: 1 }}><input inputMode="decimal" placeholder="e.g. 38.5" value={volume} onChange={(e) => setVolume(e.target.value.replace(/[^0-9.]/g, ""))} /></Field>
        <Field label="Cost (optional)" style={{ flex: 1 }}><input inputMode="decimal" placeholder="total paid" value={cost} onChange={(e) => setCost(e.target.value.replace(/[^0-9.]/g, ""))} /></Field>
      </div>
      <label className="row" style={{ gap: "0.6rem", margin: "0.25rem 0 0.9rem", fontSize: "var(--fs-sm)", cursor: "pointer" }}>
        <input type="checkbox" checked={isFull} onChange={(e) => setIsFull(e.target.checked)} style={{ width: "auto", minHeight: "auto" }} />
        Filled to full <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>(keeps economy accurate)</span>
      </label>
      <Field label="Note (optional)"><input placeholder="station, octane…" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      {err && <div className="notice notice-err" style={{ margin: "0.5rem 0 0.25rem" }}><span style={{ fontSize: "var(--fs-xs)" }}>{err}</span></div>}
      <FormActions onCancel={onClose} onSave={save} saving={saving} canSave saveLabel={isEditing ? "Update" : "Save fill-up"} />
    </BottomSheet>
  );
}

function ServiceForm({ vehicle, editing, lastOdometer, onClose, onSaved }: { vehicle: Vehicle; editing: Service | null; lastOdometer: number | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [date, setDate] = useState(editing ? editing.ts.slice(0, 10) : todayLocal());
  const [kind, setKind] = useState(editing?.kind ?? "");
  const [customKind, setCustomKind] = useState("");
  const [odometer, setOdometer] = useState(editing ? String(editing.odometer) : "");
  const [cost, setCost] = useState(editing && editing.cost > 0 ? String(editing.cost) : "");
  const [note, setNote] = useState(editing?.note ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const resolvedKind = (customKind.trim() ? normalizeKind(customKind) : kind);

  async function save() {
    const odo = parseFloat(odometer);
    if (!resolvedKind) { setErr("Pick or type what was done."); return; }
    if (Number.isNaN(odo) || odo < 0) { setErr("Enter the odometer reading."); return; }
    setSaving(true); setErr(null);
    const ts = new Date(date + "T12:00:00").toISOString();
    const payload = { kind: resolvedKind, odometer: odo, cost: cost ? Math.max(0, parseFloat(cost) || 0) : 0, note: note.trim(), ts };
    try {
      if (isEditing) await store.updateService(editing!.id, payload);
      else await store.createService({ vehicle_id: vehicle.id, ...payload });
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <BottomSheet title={isEditing ? "Edit service" : "Log service"} onClose={onClose}>
      <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--ink-2)", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>What was done?</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.6rem" }}>
        {SERVICE_KINDS.map((k) => (
          <button key={k} type="button" className={`chip${kind === k && !customKind.trim() ? " selected" : ""}`} onClick={() => { setKind(k); setCustomKind(""); }}>{prettyKind(k)}</button>
        ))}
      </div>
      <input placeholder="…or type your own" value={customKind} onChange={(e) => { setCustomKind(e.target.value); if (e.target.value.trim()) setKind(""); }} style={{ marginBottom: "0.9rem" }} />
      <div className="row" style={{ gap: "0.6rem", alignItems: "stretch", flexWrap: "nowrap" }}>
        <Field label="Date" style={{ flex: 1 }}><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label={`Odometer (${vehicle.distance_unit})`} style={{ flex: 1 }}>
          <input inputMode="decimal" placeholder={lastOdometer !== null ? `≈ ${fmtNum(lastOdometer)}` : "e.g. 42000"} value={odometer} onChange={(e) => setOdometer(e.target.value.replace(/[^0-9.]/g, ""))} />
        </Field>
      </div>
      <Field label="Cost (optional)"><input inputMode="decimal" placeholder="total paid" value={cost} onChange={(e) => setCost(e.target.value.replace(/[^0-9.]/g, ""))} /></Field>
      <Field label="Note (optional)"><input placeholder="shop, parts…" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      {err && <div className="notice notice-err" style={{ margin: "0.5rem 0 0.25rem" }}><span style={{ fontSize: "var(--fs-xs)" }}>{err}</span></div>}
      <FormActions onCancel={onClose} onSave={save} saving={saving} canSave={!!resolvedKind} saveLabel={isEditing ? "Update" : "Save service"} />
    </BottomSheet>
  );
}

function FormActions({ onCancel, onSave, saving, canSave, saveLabel }: { onCancel: () => void; onSave: () => void; saving: boolean; canSave: boolean; saveLabel: string }) {
  return (
    <div className="row" style={{ gap: "0.6rem", marginTop: "1.1rem", flexWrap: "nowrap" }}>
      <button className="btn btn-pill" style={{ flex: 1 }} onClick={onCancel} disabled={saving}>Cancel</button>
      <button className="btn btn-primary btn-pill" style={{ flex: 2 }} onClick={onSave} disabled={saving || !canSave}>{saving ? "Saving…" : saveLabel}</button>
    </div>
  );
}

function Field({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ marginBottom: "0.8rem", ...style }}>
      <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--ink-2)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      {children}
    </div>
  );
}

// Bottom sheet for forms — iOS-native feel; slides up from the bottom edge.
function BottomSheet({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet sheet-bottom" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-grab" />
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "1.1rem" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1.2rem", letterSpacing: "-0.01em" }}>{title}</div>
          <button className="tile-del" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </>
  );
}

// Centered modal — for the small destructive confirm.
function CenterModal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet sheet-center" role="dialog" aria-modal="true" aria-label={title}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.9rem" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1.15rem" }}>{title}</div>
          <button className="tile-del" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </div>
        {children}
      </div>
    </>
  );
}
